import { formatCompact, formatFunding, formatPercent, formatSpreadBps } from "@/lib/formatters";
import { ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

export type ProviderTableRow = {
  provider: string;
  markets: number | null;
  assets?: number | null;
  oiShare?: number | null;
  volumeShare?: number | null;
  oiChangePct?: number | null;
  spreadAvgBps?: number | null;
  fundingMean?: number | null;
};

export function ProviderTable({ rows }: { rows: ProviderTableRow[] }) {
  const navigate = useNavigate();

  const navigateToProvider = (provider: string) => {
    navigate(`/providers/${provider}`);
  };

  return (
    <section className="border border-border/80 bg-card/95">
      <div className="border-b border-border/80 px-3 py-2 text-xs font-mono uppercase tracking-wide text-muted-foreground">
        Provider Summary
      </div>

      <div className="w-full overflow-x-auto">
        <table className="w-max min-w-full border-collapse">
          <thead className="text-[0.7em] lg:text-xs uppercase font-mono tracking-wide text-muted-foreground">
            <tr className="border-b border-border/70">
              <th className="px-3 py-1.5 md:px-5 md:py-3 text-left">Provider</th>
              <th className="px-3 py-1.5 md:px-5 md:py-3 text-right">Markets</th>
              <th className="px-3 py-1.5 md:px-5 md:py-3 text-right">OI Share</th>
              <th className="px-3 py-1.5 md:px-5 md:py-3 text-right">Volume Share</th>
              <th className="px-3 py-1.5 md:px-5 md:py-3 text-right">OI Change</th>
              <th className="px-3 py-1.5 md:px-5 md:py-3 text-right">Avg Spread</th>
              <th className="px-3 py-1.5 md:px-5 md:py-3 text-right">Funding</th>
            </tr>
          </thead>
          <tbody>
            {!rows.length ? (
              <tr>
                <td colSpan={7} className="px-3 py-4 md:px-5 md:py-6 text-sm text-muted-foreground">
                  No provider summary data available.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.provider}
                  role="link"
                  tabIndex={0}
                  onClick={() => navigateToProvider(row.provider)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      navigateToProvider(row.provider);
                    }
                  }}
                  className="cursor-pointer border-b border-border/60 text-xs md:text-sm hover:bg-primary/5"
                >
                  <td className="p-3 lg:px-5 lg:py-4 text-sm">
                    <span className="group inline-flex items-center gap-1 font-semibold uppercase">
                      <span>{row.provider}</span>
                      <ArrowUpRight className="size-3 text-muted-foreground transition-colors group-hover:text-primary" aria-hidden="true" />
                    </span>
                  </td>
                  <td className="p-3 lg:px-5 lg:py-4 text-right text-sm font-mono">{formatCompact(row.markets)}</td>
                  <td className="p-3 lg:px-5 lg:py-4 text-right text-sm font-mono">
                    {row.oiShare == null ? "—" : formatPercent(row.oiShare * 100)}
                  </td>
                  <td className="p-3 lg:px-5 lg:py-4 text-right text-sm font-mono">
                    {row.volumeShare == null ? "—" : formatPercent(row.volumeShare * 100)}
                  </td>
                  <td className="p-3 lg:px-5 lg:py-4 text-right text-sm font-mono">{formatPercent(row.oiChangePct)}</td>
                  <td className="p-3 lg:px-5 lg:py-4 text-right text-sm font-mono">{formatSpreadBps(row.spreadAvgBps)}</td>
                  <td className="p-3 lg:px-5 lg:py-4 text-right text-sm font-mono">{formatFunding(row.fundingMean)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
